import React from 'react'
import {
  Card,
  CardMedia,
  CardContent,
  Typography,
  Box,
  Chip,
  Button,
  Divider,
  IconButton
} from '@mui/material'
import {
  LocationOn as LocationIcon,
  Bed as BedIcon,
  Bathtub as BathIcon,
  SquareFoot as SquareFootIcon,
  Star as StarIcon
} from '@mui/icons-material'
import startWhiteIcon from '../assets/start_white.png'

export interface PropertyData {
  id?: string
  image: string
  price: string
  address: string
  city: string
  state: string
  zipCode: string
  beds: number
  baths: number
  squareFeet: number
  isFeatured?: boolean
  isFavorite?: boolean
  url?: string
}

export interface PropertyCardProps {
  data: PropertyData
  onViewDetails?: (property: PropertyData) => void
  onFavorite?: (property: PropertyData) => void
  showViewDetails?: boolean // Controla la visibilidad del botón "View Details"
  showForSale?: boolean // Muestra badge "FOR SALE"
}

const PropertyCard: React.FC<PropertyCardProps> = ({
  data,
  onViewDetails,
  onFavorite,
  showViewDetails = true, // Por defecto se muestra el botón
  showForSale = false
}) => {
  const {
    image,
    price,
    address,
    city,
    state,
    zipCode,
    beds,
    baths,
    squareFeet,
    isFeatured = false,
    isFavorite = false,
      url = ''
  } = data

  const fullAddress = `${city}, ${state} ${zipCode}`

  return (
    <Card
      sx={{
        maxWidth: 400,
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 3,
        overflow: 'hidden',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)'
        }
      }}
    >
      {/* Image Section */}
      <Box sx={{ position: 'relative' }}>
        <CardMedia
          component="img"
          image={image}
          alt="Property"
          loading="lazy"
          decoding="async"
          sx={{
            objectFit: 'cover',
            width: '100%',
            height: '180px',
            transition: 'transform 0.3s ease',
            '&:hover': {
              transform: 'scale(1.05)'
            }
          }}
        />
        
        {/* Featured Badge */}
        {isFeatured && (
          <Chip
            icon={
              <img 
                src={startWhiteIcon} 
                alt="Star" 
                style={{ 
                  width: '16px', 
                  height: '16px',
                  marginLeft: '4px'
                }} 
              />
            }
            label="Featured"
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              backgroundColor: '#b08b4f',
              color: 'white',
              fontWeight: 'bold',
              fontSize: '0.75rem',
              height: 32,
              borderRadius: '0 0 8px 0',
              '& .MuiChip-icon': {
                marginLeft: '0px'
              }
            }}
          />
        )}

        {/* For Sale Badge */}
        {showForSale && !isFeatured && (
          <Chip
            label="FOR SALE"
            sx={{
              position: 'absolute',
              top: 12,
              left: 12,
              backgroundColor: '#b08b4f',
              color: 'white',
              fontWeight: 'bold',
              fontSize: '0.7rem',
              height: 28,
              borderRadius: '4px',
            }}
          />
        )}

        {/* Favorite Button */}
       {/* {onFavorite && (
          <IconButton
            onClick={() => onFavorite(data)}
            sx={{
              position: 'absolute',
              top: 12,
              right: 12,
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              color: isFavorite ? '#ff6b6b' : '#666',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 1)',
                transform: 'scale(1.1)'
              }
            }}
          >
            <StarIcon />
          </IconButton>
        )}*/}
      </Box>

      {/* Content Section */}
      <CardContent
        sx={{
          backgroundColor: '#b08b4f',
          color: '#ffffff',
          padding: '20px',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          '&:last-child': {
            paddingBottom: '20px'
          }
        }}
      >
        {/* Price */}
        <Typography
          variant="h4"
          component="div"
          sx={{
            fontWeight: 'bold',
            color: '#ffffff',
            marginBottom: '8px',
            fontSize: '1.5rem',
            textAlign: 'center'
          }}
        >
          {price}
        </Typography>

        {/* Address */}
        <Typography
          variant="body1"
          sx={{
            color: '#ffffff',
            marginBottom: '4px',
            fontSize: '0.7rem',
            fontWeight: 500,
            textAlign: 'center',
            textTransform: 'uppercase'
          }}
        >
          {address}
        </Typography>

        {/* Location */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '12px' }}>
          <LocationIcon sx={{ fontSize: '14px', marginRight: '4px', color: '#ffffff' }} />
          <Typography
            variant="body2"
            sx={{
              color: '#ffffff',
              fontSize: '0.65rem',
              textAlign: 'center'
            }}
          >
            {fullAddress}
          </Typography>
        </Box>

        {/* Divider */}
        <Divider
          sx={{
            backgroundColor: '#ffffff',
            marginBottom: '12px',
            opacity: 0.3
          }}
        />

        {/* Property Features */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: '16px',
            gap: '8px'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            <BedIcon sx={{ fontSize: '16px', marginRight: '4px', color: '#ffffff' }} />
            <Typography variant="body2" sx={{ color: '#ffffff', fontSize: '0.65rem' }}>
              {beds} Beds
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            <BathIcon sx={{ fontSize: '16px', marginRight: '4px', color: '#ffffff' }} />
            <Typography variant="body2" sx={{ color: '#ffffff', fontSize: '0.65rem' }}>
              {baths} Baths
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            <SquareFootIcon sx={{ fontSize: '16px', marginRight: '4px', color: '#ffffff' }} />
            <Typography variant="body2" sx={{ color: '#ffffff', fontSize: '0.65rem' }}>
              {squareFeet.toLocaleString()} sqft
            </Typography>
          </Box>
        </Box>

        {/* View Details Button */}
        <Box sx={{ mt: 'auto' }}>
          {showViewDetails && (
            <Button
              variant="outlined"
              onClick={() => onViewDetails?.(data)}
              sx={{
                width: '100%',
                backgroundColor: 'transparent',
                borderColor: '#ffffff',
                color: '#ffffff',
                borderRadius: '8px',
                padding: '8px 16px',
                fontSize: '0.7rem',
                fontWeight: 700,
                textTransform: 'none',
                fontFamily: 'Montserrat, sans-serif',
                border: '2px solid #ffffff',
                transition: 'all 0.3s ease',
                minHeight: 'auto',
                height: 'auto',
                '&:hover': {
                  backgroundColor: '#ffffff',
                  color: '#b08b4f',
                  borderColor: '#ffffff'
                }
              }}
            >
              View Details
            </Button>
          )}
        </Box>
      </CardContent>
    </Card>
  )
}

export default PropertyCard
